import * as React from 'react'

import { Box, SearchField, Text } from '@factorialco/deprecated-design-system'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import ScrollableList from 'modules/core/components/ScrollableList'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { useNineGridBox } from 'modules/competencies/lib/ninegridboxContext'
import ninegridboxes from 'modules/competencies/store/collections/ninegridboxes'

import CompetencyChild from './Child'
import CompetencyParent from './Parent'

interface Props {
  isXaxis: boolean
}

const Content = ({ isXaxis }: Props) => {
  const { search, onSearch } = useDebouncedSearch()
  const { selectableCompetencyIds, form } = useNineGridBox()
  const isFiltered = search?.length > 0
  const selectedCompetenciesForThisAxis = form.get(isXaxis ? 'x_axis_ids' : 'y_axis_ids').value
  const displayedCompetenciesPerAxis = uniq([
    ...selectedCompetenciesForThisAxis,
    ...selectableCompetencyIds,
  ])

  const filteredCompetencyIds = isFiltered
    ? displayedCompetenciesPerAxis.filter((competencyId) => {
        const ninegridbox = ninegridboxes.getCompetency(competencyId)

        return ninegridbox && matchText(search, ninegridbox.competencyName)
      })
    : displayedCompetenciesPerAxis

  const parentCompetencyIds = filteredCompetencyIds.filter(
    (competencyId) => ninegridboxes.getCompetency(competencyId)?.parentId === null
  )

  const childrenWithoutParentsIds = filteredCompetencyIds.filter((competencyId) => {
    const parentId = ninegridboxes.getCompetency(competencyId)?.parentId

    return parentId && !parentCompetencyIds?.includes(parentId)
  })

  const CompetencyList = isFiltered ? CompetencyChild : CompetencyParent
  const computedCompetencyIds: number[] = isFiltered ? filteredCompetencyIds : parentCompetencyIds

  return (
    <Box
      width="full"
      background="grey200"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
      overflowY="hidden"
    >
      <Box flexGrow paddingY="s16" paddingX="s24">
        <SearchField
          variant="big"
          search={search}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      </Box>
      <ScrollableList>
        {!isFiltered &&
          childrenWithoutParentsIds.length > 0 &&
          childrenWithoutParentsIds.map((competencyId) => (
            <CompetencyChild competencyId={competencyId} key={competencyId} isXaxis={isXaxis} />
          ))}
        {computedCompetencyIds.length > 0 &&
          computedCompetencyIds.map((competencyId) => (
            <CompetencyList competencyId={competencyId} key={competencyId} isXaxis={isXaxis} />
          ))}
      </ScrollableList>
      {computedCompetencyIds.length === 0 && childrenWithoutParentsIds.length === 0 && (
        <Box paddingY="s48" paddingX="s48" flexDirection="row" gap="s16" background="grey000White">
          <Text.Normal>
            {i18n.t('app_store_v2.apps.competencies_v2.settings.competencies.search_empty')}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default observer(Content)
